'use client';

import { Await, useFetcher, useLocation, useMatches } from 'react-router';
import {
    type ReactElement,
    type KeyboardEvent,
    useRef,
    useEffect,
    useLayoutEffect,
    useCallback,
    useState,
    lazy,
    Suspense,
    useMemo,
} from 'react';
import { Link } from '@/components/link';
import { ForesightLink } from '@/components/link/foresight-link';
import { useTranslation } from 'react-i18next';
import logo from '/images/logo.svg';
import EatalyUserActions from './user-actions/eataly-user-actions';
import CartBadge from './cart-badge';
import SearchBar from './search';
import { cn } from '@/lib/utils';

import { handleHeaderArrowNavigation } from './header-keyboard-navigation';
import EatalyHeaderNavTrigger from './eataly-header-nav-trigger';
import EatalyFullscreenNavOverlay from './eataly-fullscreen-nav-overlay';
import type { PageWithComponentData } from '@/lib/util/pageLoader.server';
import type { ShopperProducts } from '@/scapi';
import type { ContentFolder } from '@/lib/api/content-folders.server';
import { Button } from '../ui/button';
import { SvgStore } from '../icons-eataly';
import { useRouteTransitionReady } from 'src/hooks/use-route-transition-ready';
import { useEffectivePrimaryCategoryPath } from '@/hooks/use-effective-primary-category-path';
import type { PrimaryShoppingCategoryResponse } from '@/routes/resource.primary-shopping-category';
import { focusVisibleOutline } from 'src/lib/focus-styles';
import { EatalyHeaderChromeProvider } from './eataly-header-chrome-context';
import { createCatalogMenuDataPromise } from '@/lib/catalog-menu-data';
import type { CategoryProductCounts } from '@/lib/category-product-counts';
import { findContentFolderById } from 'src/lib/content-folders';
import type { NavigationPillarRootIds } from '@/lib/navigation-pillars';

const EditorialMenuContent = lazy(() => import('@/components/editorial-menu/editorial-menu-content'));
const CatalogMenuContent = lazy(() => import('./catalog-menu-content'));

const HOME_ROUTE_ID = 'routes/_app._index';

const SCROLL_DIRECTION_THRESHOLD_PX = 8;
const ALWAYS_SHOW_BELOW_SCROLL_Y = 90;

export type EatalyHeaderProps = {
    storesFolderIdFromSitePreferences?: string;
    navigationPillarRootIds: NavigationPillarRootIds;
    editorialMenuPage?: Promise<PageWithComponentData | null>;
    catalogTree?: Promise<ShopperProducts.schemas['Category']>;
    contentFolders?: Promise<ContentFolder[]>;
    categoryProductCounts?: Promise<CategoryProductCounts | undefined>;
};

export default function EatalyHeader({
    storesFolderIdFromSitePreferences,
    navigationPillarRootIds,
    editorialMenuPage,
    catalogTree,
    contentFolders,
    categoryProductCounts,
}: EatalyHeaderProps): ReactElement {
    const { t } = useTranslation('header');
    const location = useLocation();
    const matches = useMatches();
    const isHomePage = matches.some((m) => m.id === HOME_ROUTE_ID);
    const routeKey = `${location.pathname}${location.search}${location.hash}`;
    const isTransitionReady = useRouteTransitionReady(routeKey);
    const headerRef = useRef<HTMLElement>(null);
    const lastScrollY = useRef(0);
    const [desktopSearchModalOpen, setDesktopSearchModalOpen] = useState(false);
    const [headerHidden, setHeaderHidden] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuClosing, setMenuClosing] = useState(false);
    const [menuEverOpened, setMenuEverOpened] = useState(false);
    const primaryCategoryFetcher = useFetcher<PrimaryShoppingCategoryResponse>();
    const resolvedFromRoute = useEffectivePrimaryCategoryPath();
    const pillarPath = resolvedFromRoute ?? primaryCategoryFetcher.data?.primaryCategoryPath ?? null;

    const homeTo = useMemo(() => pillarPath ?? '/', [pillarPath]);
    const catalogPrimaryPath = useMemo(() => pillarPath ?? '/', [pillarPath]);
    const catalogMenuDataPromise = useMemo(
        () => createCatalogMenuDataPromise(catalogTree, contentFolders, categoryProductCounts),
        [catalogTree, contentFolders, categoryProductCounts]
    );

    const prevResolvedFromRoute = useRef(resolvedFromRoute);

    useEffect(() => {
        const wasOnPlp = prevResolvedFromRoute.current != null;
        const isOffPlp = resolvedFromRoute == null;

        prevResolvedFromRoute.current = resolvedFromRoute;

        if (!wasOnPlp || !isOffPlp) return;
        if (primaryCategoryFetcher.state !== 'idle') return;

        void primaryCategoryFetcher.load('/resource/primary-shopping-category');
    }, [resolvedFromRoute, primaryCategoryFetcher]);

    const openMenu = useCallback(() => {
        if (menuClosing) return;
        setMenuEverOpened(true);
        setMenuOpen(true);
    }, [menuClosing]);

    const closeMenu = useCallback(() => {
        setMenuClosing(true);
        setMenuOpen(false);
    }, []);

    const handleMenuExitComplete = useCallback(() => {
        setMenuClosing(false);
    }, []);

    // Avoid listing `menuOpen` as a dep — that would close as soon as it opens.
    const menuOpenRef = useRef(menuOpen);
    menuOpenRef.current = menuOpen;

    // Close fullscreen nav on page change (editorial splash links omit onClose).
    useEffect(() => {
        if (!menuOpenRef.current) return;
        closeMenu();
    }, [location.pathname, closeMenu]);

    useEffect(() => {
        if (!menuOpen) return;
        const onKeyDown = (e: globalThis.KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeMenu();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [menuOpen, closeMenu]);

    const syncHeaderChromeCssVars = useCallback((hidden: boolean) => {
        if (!headerRef.current) {
            return;
        }

        const height = `${headerRef.current.offsetHeight}px`;
        const visibleOffset = hidden ? '0px' : height;

        headerRef.current.style.setProperty('--header-height', height);
        headerRef.current.style.setProperty('--header-visible-offset', visibleOffset);
        document.documentElement.style.setProperty('--header-height', height);
        document.documentElement.style.setProperty('--header-visible-offset', visibleOffset);
    }, []);

    useLayoutEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        syncHeaderChromeCssVars(headerHidden);
        const observer = new ResizeObserver(() => syncHeaderChromeCssVars(headerHidden));
        observer.observe(el);
        return () => observer.disconnect();
    }, [headerHidden, syncHeaderChromeCssVars]);

    const applyScrollChrome = useCallback(() => {
        const y = window.scrollY;

        if (desktopSearchModalOpen) {
            setHeaderHidden(false);
        } else {
            const delta = y - lastScrollY.current;
            if (y <= ALWAYS_SHOW_BELOW_SCROLL_Y) {
                setHeaderHidden(false);
            } else if (delta > SCROLL_DIRECTION_THRESHOLD_PX) {
                setHeaderHidden(true);
            } else if (delta < -SCROLL_DIRECTION_THRESHOLD_PX) {
                setHeaderHidden(false);
            }
        }

        lastScrollY.current = y;
    }, [desktopSearchModalOpen]);

    useLayoutEffect(() => {
        applyScrollChrome();
    }, [applyScrollChrome, routeKey]);

    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    applyScrollChrome();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [applyScrollChrome]);
    const searchKeyPrefix = `${location.pathname}${location.search}`;

    const onHeaderKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
        const el = headerRef.current;
        if (!el) return;
        handleHeaderArrowNavigation(el, e);
    }, []);

    const storeLocatorButton = (href: string) => (
        <Button
            asChild
            hierarchy="tertiary"
            iconOnly
            size="icon"
            className="cursor-pointer"
            aria-label={t('store.ariaLabel')}>
            <Link to={href}>
                <SvgStore className="size-6" aria-hidden />
            </Link>
        </Button>
    );

    return (
        <header
            ref={headerRef}
            data-testid="eataly-header"
            data-header-hidden={headerHidden ? 'true' : 'false'}
            onKeyDown={onHeaderKeyDown}
            className={cn(
                'sticky top-0 left-0 right-0 z-50 bg-neutral-white text-foreground',
                'will-change-transform motion-reduce:transition-none',
                isTransitionReady && 'transition-transform duration-200 ease-out',
                headerHidden && 'pointer-events-none -translate-y-full'
            )}>
            <EatalyHeaderChromeProvider headerHidden={headerHidden}>
                <div
                    className={cn(
                        'flex flex-col lg:grid lg:min-h-[72px] lg:items-center lg:gap-6 lg:px-6 lg:py-3',
                        desktopSearchModalOpen ? 'lg:grid-cols-[auto_auto_1fr]' : 'lg:grid-cols-[1fr_auto_1fr]'
                    )}>
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-3 lg:contents">
                        <div
                            className="flex min-w-0 items-center gap-3 justify-self-start lg:gap-4"
                            data-testid="eataly-header-nav">
                            <EatalyHeaderNavTrigger
                                open={menuOpen}
                                menuClosing={menuClosing}
                                onOpen={openMenu}
                                onClose={closeMenu}
                            />
                            <Suspense fallback={<div className="shrink-0">{storeLocatorButton('/')}</div>}>
                                <Await resolve={contentFolders}>
                                    {(folders) => {
                                        if (!folders) return <div className="shrink-0">{storeLocatorButton('/')}</div>;
                                        const storeLocatorHref = storesFolderIdFromSitePreferences
                                            ? findContentFolderById(folders, storesFolderIdFromSitePreferences)?.path
                                            : undefined;
                                        return (
                                            <div className="shrink-0">
                                                {storeLocatorButton(storeLocatorHref ?? '/')}
                                            </div>
                                        );
                                    }}
                                </Await>
                            </Suspense>
                        </div>
                        <ForesightLink
                            to={homeTo}
                            hitSlop={20}
                            name="header:logo"
                            className={cn(
                                'flex max-w-[124px] shrink-0 items-center justify-center justify-self-center rounded-md lg:mx-4 lg:max-w-none',
                                focusVisibleOutline
                            )}
                            data-testid="header-logo"
                            aria-label={t('logoAlt')}>
                            <img
                                src={logo}
                                alt=""
                                className={cn(
                                    'h-4 w-auto max-h-4 object-contain [filter:var(--header-logo-filter)]',
                                    'lg:h-6 lg:max-w-[186px]'
                                )}
                            />
                        </ForesightLink>
                        <div className="flex items-center justify-end gap-0.5 justify-self-end lg:hidden">
                            <EatalyUserActions />
                            <CartBadge />
                        </div>
                    </div>

                    <div
                        className={cn(
                            'hidden min-w-0 items-center justify-end gap-6 lg:gap-4 xl:gap-6 justify-self-end lg:flex',
                            desktopSearchModalOpen && 'lg:w-full'
                        )}>
                        <div
                            className={cn(
                                'min-w-0 max-w-[200px] lg:transition-[flex-1,max-width,width] lg:duration-300 lg:ease-out',
                                desktopSearchModalOpen
                                    ? 'lg:flex-1 lg:max-w-none'
                                    : 'lg:w-full lg:max-w-[300px] xl:max-w-sm lg:shrink'
                            )}
                            data-testid="header-search-desktop">
                            <SearchBar
                                key={`${searchKeyPrefix}-desktop`}
                                variant="desktop"
                                onActiveChange={setDesktopSearchModalOpen}
                            />
                        </div>
                        <div className="shrink-0 hidden lg:block">
                            <EatalyUserActions />
                        </div>
                        <div className="shrink-0 hidden lg:block">
                            <CartBadge />
                        </div>
                    </div>

                    <div className="px-4 pb-3 pt-0 lg:hidden" data-testid="header-search-mobile">
                        <SearchBar key={`${searchKeyPrefix}-mobile`} variant="mobile" />
                    </div>
                </div>

                <EatalyFullscreenNavOverlay
                    open={menuOpen}
                    onClose={closeMenu}
                    onExitComplete={handleMenuExitComplete}
                    isEditorialMenu={isHomePage}>
                    {menuEverOpened && (
                        <Suspense>
                            {isHomePage ? (
                                <EditorialMenuContent page={editorialMenuPage} />
                            ) : (
                                <CatalogMenuContent
                                    menuDataPromise={catalogMenuDataPromise}
                                    navigationPillarRootIds={navigationPillarRootIds}
                                    onClose={closeMenu}
                                    primaryCategoryPath={catalogPrimaryPath}
                                />
                            )}
                        </Suspense>
                    )}
                </EatalyFullscreenNavOverlay>
            </EatalyHeaderChromeProvider>
        </header>
    );
}
